/**
 * Treasury Service - API Client
 * Handles all treasury-related API calls
 */
import api from '@/utils/api';
import type {
  CashForecast,
  CashForecastSummary,
  CashAlert,
  CashRecommendation,
  RecurringExpense,
  RecurringExpenseOccurrence,
  Budget,
  FinancialHealthScore,
  TreasuryDashboardData,
  GetForecastsParams,
  CreateForecastParams,
  UpdateForecastParams,
  CreateRecurringExpenseParams,
  UpdateRecurringExpenseParams,
  CreateBudgetParams,
  UpdateBudgetParams,
  ForecastScenario,
  SimulationScenarioParams,
  SimulationResult,
  SimulationTemplate,
  ScenarioComparisonResult,
} from '../types/treasury.types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const treasuryService = {
  // ==================== DASHBOARD ====================

  /**
   * Get treasury dashboard data
   */
  async getDashboard(companyId: string): Promise<TreasuryDashboardData> {
    const response = await api.get(`/treasury/dashboard?companyId=${companyId}`) as ApiResponse<TreasuryDashboardData>;
    return response.data;
  },

  /**
   * Get treasury metrics/summary
   */
  async getMetrics(companyId: string, period?: string): Promise<CashForecastSummary> {
    const params = new URLSearchParams({ companyId });
    if (period) params.append('period', period);
    const response = await api.get(`/treasury/metrics?${params.toString()}`) as ApiResponse<CashForecastSummary>;
    return response.data;
  },

  // ==================== FORECASTS ====================

  /**
   * Get all cash forecasts
   */
  async getForecasts(params: GetForecastsParams): Promise<CashForecast[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('companyId', params.companyId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.scenario) queryParams.append('scenario', params.scenario);
    if (params.alertLevel) queryParams.append('alertLevel', params.alertLevel);

    const response = await api.get(`/treasury/forecasts?${queryParams.toString()}`) as ApiResponse<CashForecast[]>;
    return response.data;
  },

  /**
   * Get forecast by ID
   */
  async getForecastById(id: string, companyId: string): Promise<CashForecast> {
    const response = await api.get(`/treasury/forecasts/${id}?companyId=${companyId}`) as ApiResponse<CashForecast>;
    return response.data;
  },

  /**
   * Get forecast summary
   */
  async getForecastSummary(
    companyId: string,
    startDate?: string,
    endDate?: string
  ): Promise<CashForecastSummary> {
    const params = new URLSearchParams({ companyId });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/treasury/forecasts/summary?${params.toString()}`) as ApiResponse<CashForecastSummary>;
    return response.data;
  },

  /**
   * Create a new forecast
   */
  async createForecast(params: CreateForecastParams): Promise<CashForecast> {
    const response = await api.post('/treasury/forecasts', params) as ApiResponse<CashForecast>;
    return response.data;
  },

  /**
   * Update a forecast
   */
  async updateForecast(
    id: string,
    companyId: string,
    params: UpdateForecastParams
  ): Promise<CashForecast> {
    const response = await api.put(`/treasury/forecasts/${id}`, {
      companyId,
      ...params,
    }) as ApiResponse<CashForecast>;
    return response.data;
  },

  /**
   * Delete a forecast
   */
  async deleteForecast(id: string, companyId: string): Promise<void> {
    await api.delete(`/treasury/forecasts/${id}?companyId=${companyId}`);
  },

  /**
   * Generate automatic forecasts using ML
   */
  async generateAutoForecasts(
    companyId: string,
    daysAhead: number = 90,
    scenario?: ForecastScenario
  ): Promise<CashForecast[]> {
    const response = await api.post('/treasury/forecasts/generate', {
      companyId,
      daysAhead,
      scenario,
    }) as ApiResponse<CashForecast[]>;
    return response.data;
  },

  // ==================== ALERTS ====================

  /**
   * Get all alerts
   */
  async getAlerts(companyId: string, status?: string): Promise<CashAlert[]> {
    const params = new URLSearchParams({ companyId });
    if (status) params.append('status', status);
    const response = await api.get(`/treasury/alerts?${params.toString()}`) as ApiResponse<CashAlert[]>;
    return response.data;
  },

  /**
   * Acknowledge an alert
   */
  async acknowledgeAlert(id: string, companyId: string): Promise<CashAlert> {
    const response = await api.post(`/treasury/alerts/${id}/acknowledge`, { companyId }) as ApiResponse<CashAlert>;
    return response.data;
  },

  /**
   * Resolve an alert
   */
  async resolveAlert(id: string, companyId: string, notes?: string): Promise<CashAlert> {
    const response = await api.post(`/treasury/alerts/${id}/resolve`, { companyId, notes }) as ApiResponse<CashAlert>;
    return response.data;
  },

  /**
   * Snooze an alert
   */
  async snoozeAlert(id: string, companyId: string, until: string): Promise<CashAlert> {
    const response = await api.post(`/treasury/alerts/${id}/snooze`, { companyId, until }) as ApiResponse<CashAlert>;
    return response.data;
  },

  // ==================== RECOMMENDATIONS ====================

  /**
   * Get all recommendations
   */
  async getRecommendations(companyId: string, status?: string): Promise<CashRecommendation[]> {
    const params = new URLSearchParams({ companyId });
    if (status) params.append('status', status);
    const response = await api.get(`/treasury/recommendations?${params.toString()}`) as ApiResponse<CashRecommendation[]>;
    return response.data;
  },

  /**
   * Apply a recommendation
   */
  async applyRecommendation(id: string, companyId: string): Promise<CashRecommendation> {
    const response = await api.post(`/treasury/recommendations/${id}/apply`, { companyId }) as ApiResponse<CashRecommendation>;
    return response.data;
  },

  /**
   * Dismiss a recommendation
   */
  async dismissRecommendation(
    id: string,
    companyId: string,
    reason?: string
  ): Promise<CashRecommendation> {
    const response = await api.post(`/treasury/recommendations/${id}/dismiss`, {
      companyId,
      reason,
    }) as ApiResponse<CashRecommendation>;
    return response.data;
  },

  // ==================== RECURRING EXPENSES ====================

  /**
   * Get all recurring expenses
   */
  async getRecurringExpenses(companyId: string): Promise<RecurringExpense[]> {
    // Toujours inclure les inactives - le filtrage se fait côté frontend
    const response = await api.get(`/treasury/recurring-expenses?companyId=${companyId}&includeInactive=true`) as ApiResponse<RecurringExpense[]>;
    return response.data;
  },

  /**
   * Get recurring expense by ID
   */
  async getRecurringExpenseById(id: string, companyId: string): Promise<RecurringExpense> {
    const response = await api.get(`/treasury/recurring-expenses/${id}?companyId=${companyId}`) as ApiResponse<RecurringExpense>;
    return response.data;
  },

  /**
   * Create a recurring expense
   */
  async createRecurringExpense(params: CreateRecurringExpenseParams): Promise<RecurringExpense> {
    const response = await api.post('/treasury/recurring-expenses', params) as ApiResponse<RecurringExpense>;
    return response.data;
  },

  /**
   * Update a recurring expense
   */
  async updateRecurringExpense(
    id: string,
    companyId: string,
    params: UpdateRecurringExpenseParams
  ): Promise<RecurringExpense> {
    const response = await api.put(`/treasury/recurring-expenses/${id}`, {
      companyId,
      ...params,
    }) as ApiResponse<RecurringExpense>;
    return response.data;
  },

  /**
   * Delete a recurring expense
   */
  async deleteRecurringExpense(id: string, companyId: string): Promise<void> {
    await api.delete(`/treasury/recurring-expenses/${id}?companyId=${companyId}`);
  },

  // NOTE: The following endpoints do not exist on the backend yet.
  // They are kept as stubs for future implementation.

  /**
   * Generate occurrences for a recurring expense
   * GHOST ENDPOINT: Backend route /treasury/recurring-expenses/:id/generate-occurrences does not exist yet
   */
  async generateOccurrences(
    _id: string,
    _companyId: string,
    _untilDate: string
  ): Promise<RecurringExpenseOccurrence[]> {
    // Backend endpoint not implemented - return empty array
    return [];
  },

  /**
   * Mark occurrence as paid
   * GHOST ENDPOINT: Backend route /treasury/recurring-expenses/occurrences/:id/pay does not exist yet
   */
  async markOccurrencePaid(
    _occurrenceId: string,
    _companyId: string,
    _actualAmount?: number
  ): Promise<RecurringExpenseOccurrence> {
    throw new Error('Backend endpoint /treasury/recurring-expenses/occurrences/:id/pay not implemented');
  },

  /**
   * Get upcoming occurrences
   * GHOST ENDPOINT: Backend route /treasury/recurring-expenses/upcoming does not exist yet
   */
  async getUpcomingOccurrences(_companyId: string, _days: number = 30): Promise<RecurringExpenseOccurrence[]> {
    // Backend endpoint not implemented - return empty array
    return [];
  },

  // ==================== BUDGETS ====================

  /**
   * Get all budgets
   */
  async getBudgets(companyId: string, status?: string): Promise<Budget[]> {
    const params = new URLSearchParams({ companyId });
    if (status) params.append('status', status);
    const response = await api.get(`/treasury/budgets?${params.toString()}`) as ApiResponse<Budget[]>;
    return response.data;
  },

  /**
   * Get budget by ID
   */
  async getBudgetById(id: string, companyId: string): Promise<Budget> {
    const response = await api.get(`/treasury/budgets/${id}?companyId=${companyId}`) as ApiResponse<Budget>;
    return response.data;
  },

  /**
   * Create a budget
   */
  async createBudget(params: CreateBudgetParams): Promise<Budget> {
    const response = await api.post('/treasury/budgets', params) as ApiResponse<Budget>;
    return response.data;
  },

  /**
   * Update a budget
   */
  async updateBudget(
    id: string,
    companyId: string,
    params: UpdateBudgetParams
  ): Promise<Budget> {
    const response = await api.put(`/treasury/budgets/${id}`, {
      companyId,
      ...params,
    }) as ApiResponse<Budget>;
    return response.data;
  },

  /**
   * Delete a budget
   */
  async deleteBudget(id: string, companyId: string): Promise<void> {
    await api.delete(`/treasury/budgets/${id}?companyId=${companyId}`);
  },

  /**
   * Get budget analysis
   * GHOST ENDPOINT: Backend route /treasury/budgets/:id/analysis does not exist yet
   */
  async getBudgetAnalysis(_id: string, _companyId: string): Promise<Record<string, unknown>> {
    // Backend endpoint not implemented - return empty object
    return {};
  },

  // ==================== FINANCIAL HEALTH ====================

  /**
   * Get financial health score
   */
  async getHealthScore(companyId: string): Promise<FinancialHealthScore> {
    const response = await api.get(`/treasury/health-score?companyId=${companyId}`) as ApiResponse<FinancialHealthScore>;
    return response.data;
  },

  /**
   * Get health score history
   */
  async getHealthScoreHistory(
    companyId: string,
    months: number = 6
  ): Promise<FinancialHealthScore[]> {
    const response = await api.get(`/treasury/health-score/history?companyId=${companyId}&months=${months}`) as ApiResponse<FinancialHealthScore[]>;
    return response.data;
  },

  // ==================== ML / AI INTELLIGENCE ====================

  /**
   * Generate ML-powered forecasts using OpenAI GPT-4
   */
  async generateMLForecasts(
    companyId: string,
    daysAhead: number = 90
  ): Promise<MLForecastResult> {
    const response = await api.post('/treasury/ml/forecasts', {
      companyId,
      daysAhead,
    }) as ApiResponse<MLForecastResult>;
    return response.data;
  },

  /**
   * Generate ML-powered recommendations
   */
  async generateMLRecommendations(companyId: string): Promise<MLRecommendation[]> {
    const response = await api.post('/treasury/ml/recommendations', {
      companyId,
    }) as ApiResponse<MLRecommendation[]>;
    return response.data;
  },

  /**
   * Get ML health score analysis
   */
  async getMLHealthScore(companyId: string): Promise<MLHealthScore> {
    const response = await api.get(`/treasury/ml/health-score?companyId=${companyId}`) as ApiResponse<MLHealthScore>;
    return response.data;
  },

  /**
   * Get client payment risk analysis
   */
  async getClientPaymentRisk(companyId: string, clientId: string): Promise<ClientRiskAnalysis> {
    const response = await api.get(`/treasury/ml/client-risk/${clientId}?companyId=${companyId}`) as ApiResponse<ClientRiskAnalysis>;
    return response.data;
  },

  /**
   * Get ML service status
   */
  async getMLStatus(companyId: string): Promise<MLServiceStatus> {
    const response = await api.get(`/treasury/ml/status?companyId=${companyId}`) as ApiResponse<MLServiceStatus>;
    return response.data;
  },

  // ==================== ADVANCED SCORING (Phase 6.2) ====================

  /**
   * Get advanced multi-factor client scoring (BTP-optimized)
   * GHOST ENDPOINT: Backend route /treasury/ml/advanced-scoring does not exist yet
   */
  async getAdvancedClientScoring(
    _companyId: string,
    _clientId: string,
    _paymentHistory: ClientPaymentHistoryData[],
    _invoiceData: ClientInvoiceData[],
    _options?: AdvancedScoringOptions
  ): Promise<AdvancedClientScoringResult> {
    throw new Error('Backend endpoint /treasury/ml/advanced-scoring not implemented');
  },

  /**
   * Get batch advanced scoring for multiple clients
   * GHOST ENDPOINT: Backend route /treasury/ml/advanced-scoring/batch does not exist yet
   */
  async getBatchAdvancedScoring(
    _companyId: string,
    _clients: Array<{
      clientId: string;
      paymentHistory: ClientPaymentHistoryData[];
      invoiceData: ClientInvoiceData[];
    }>,
    _options?: AdvancedScoringOptions
  ): Promise<AdvancedClientScoringResult[]> {
    throw new Error('Backend endpoint /treasury/ml/advanced-scoring/batch not implemented');
  },

  // ==================== MONTE CARLO SIMULATION (Phase 6.3) ====================

  /**
   * Run Monte Carlo simulation for a scenario
   */
  async runSimulation(
    companyId: string,
    params: SimulationScenarioParams
  ): Promise<SimulationResult> {
    const response = await api.post('/treasury/ml/simulation', {
      scenario_type: params.scenarioType,
      scenario_name: params.scenarioName,
      monthly_cost: params.monthlyCost || 0,
      monthly_revenue: params.monthlyRevenue || 0,
      initial_investment: params.initialInvestment || 0,
      ramp_up_months: params.rampUpMonths || 0,
      duration_months: params.durationMonths || 12,
      client_revenue_share: params.clientRevenueShare || 0,
      price_increase_percent: params.priceIncreasePercent || 0,
      churn_probability: params.churnProbability || 0,
      current_balance: params.currentBalance || 0,
    }) as ApiResponse<any>;

    // Backend returns camelCase directly
    const data = response.data || response;
    return {
      success: data.success ?? true,
      scenarioType: data.scenarioType || params.scenarioType,
      scenarioName: data.scenarioName || params.scenarioName,
      numSimulations: data.numSimulations || 1000,
      breakEvenMonths: data.breakEvenMonths || { p10: 0, p50: 0, p90: 0 },
      roiYear1: data.roiYear1 || { p10: 0, p50: 0, p90: 0 },
      probabilityOfDeficit: data.probabilityOfDeficit || 0,
      var95: data.var95 || 0,
      monthlyProjections: (data.monthlyProjections || []).map((p: any) => ({
        month: p.month,
        date: p.date,
        balanceP10: p.balanceP10,
        balanceP50: p.balanceP50,
        balanceP90: p.balanceP90,
      })),
      warnings: data.warnings || [],
      recommendations: data.recommendations || [],
      finalBalance: data.finalBalance,
      error: data.error,
    };
  },

  /**
   * Get simulation templates
   * GHOST ENDPOINT: Backend route /treasury/ml/simulation/templates does not exist yet
   */
  async getSimulationTemplates(): Promise<SimulationTemplate[]> {
    // Backend endpoint not implemented - return empty array
    return [];
  },

  /**
   * Compare multiple scenarios
   * GHOST ENDPOINT: Backend route /treasury/ml/simulation/compare does not exist yet
   */
  async compareScenarios(
    _companyId: string,
    _scenarios: SimulationScenarioParams[],
    _currentBalance: number
  ): Promise<ScenarioComparisonResult> {
    // Backend endpoint not implemented - return empty result
    return {
      success: false,
      results: [],
      recommendation: 'Scenario comparison endpoint not yet available',
      comparisonSummary: {
        totalScenarios: 0,
        bestRoiScenario: '',
        bestRoiValue: 0,
        safestScenario: '',
        lowestRiskProbability: 0,
      },
      error: 'Backend endpoint /treasury/ml/simulation/compare not implemented',
    };
  },

  // ==================== CONFIG ====================

  /**
   * Get treasury configuration for a company
   */
  async getConfig(companyId: string): Promise<TreasuryConfig> {
    const response = await api.get(`/treasury/config?companyId=${companyId}`) as ApiResponse<TreasuryConfig>;
    return response.data;
  },

  /**
   * Update treasury configuration for a company
   */
  async updateConfig(companyId: string, config: Partial<TreasuryConfig>): Promise<TreasuryConfig> {
    const response = await api.put(`/treasury/config?companyId=${companyId}`, config) as ApiResponse<TreasuryConfig>;
    return response.data;
  },
};

// ==================== ML TYPES ====================

export interface MLForecastResult {
  forecasts: CashForecast[];
  insights: string[];
  confidence: number;
  modelVersion: string;
}

export interface MLRecommendation {
  id: string;
  type: string;
  priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  estimatedImpact: number;
  suggestedActions: string[];
  confidence: number;
  aiGenerated: boolean;
}

export interface MLHealthScore {
  overallScore: number;
  trend: 'UP' | 'DOWN' | 'STABLE';
  components: {
    liquidity: number;
    cashFlow: number;
    collectionEfficiency: number;
    paymentBehavior: number;
  };
  insights: string[];
  recommendations: string[];
  aiGenerated: boolean;
}

export interface ClientRiskAnalysis {
  clientId: string;
  clientName: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  riskScore: number;
  paymentHistory: {
    avgDaysLate: number;
    latePaymentRate: number;
    totalInvoices: number;
    paidOnTime: number;
  };
  prediction: {
    likelihoodToPayLate: number;
    estimatedDaysLate: number;
    suggestedCreditLimit: number;
  };
  recommendations: string[];
}

export interface MLServiceStatus {
  openaiEnabled: boolean;
  aiRecommendationsEnabled: boolean;
  lastForecastGeneration: string | null;
  lastRecommendationGeneration: string | null;
  totalForecastsGenerated: number;
  totalRecommendationsGenerated: number;
  modelVersion: string;
}

// ==================== CONFIG TYPES ====================

export interface TreasuryConfig {
  id: string;
  companyId: string;
  alertThresholdCritical: number;
  alertThresholdWarning: number;
  forecastHorizonDays: number;
  recalculationFrequency: string;
  emailAlertsEnabled: boolean;
  aiRecommendationsEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// ==================== ADVANCED SCORING TYPES (Phase 6.2) ====================

export interface ClientPaymentHistoryData {
  invoiceId: string;
  amount: number;
  dueDate: string;
  paymentDate: string | null;
  daysLate: number;
}

export interface ClientInvoiceData {
  invoiceId: string;
  amount: number;
  issueDate: string;
  status: 'PAID' | 'PENDING' | 'OVERDUE';
}

export interface AdvancedScoringOptions {
  sector?: 'BTP' | 'SERVICES' | 'COMMERCE' | 'INDUSTRY';
  isPublicSector?: boolean;
}

export interface AdvancedClientScoringResult {
  clientId: string;
  finalScore: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  segment: 'PREMIUM' | 'STANDARD' | 'AT_RISK' | 'PROBLEMATIC';
  components: {
    paymentHistory: {
      score: number;
      weight: number;
      avgDelayDays: number;
      onTimeRate: number;
      volatility: number;
    };
    invoicePatterns: {
      score: number;
      weight: number;
      amountCorrelation: number;
      frequency: string;
    };
    sectorRisk: {
      score: number;
      weight: number;
      seasonalFactor: number;
      sectorType: string;
    };
    trend: {
      score: number;
      weight: number;
      direction: 'IMPROVING' | 'STABLE' | 'DECLINING';
      confidence: number;
    };
    concentrationRisk: {
      score: number;
      weight: number;
      revenueShare: number;
      dependencyLevel: string;
    };
  };
  recommendations: string[];
  suggestedActions: Array<{
    action: string;
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    estimatedImpact: string;
  }>;
  btpFactors: {
    winterSlowdown: boolean;
    seasonalAdjustment: number;
    publicSectorBonus: number;
  };
  aiGenerated: boolean;
  modelVersion: string;
  generatedAt: string;
}
